/**
 * TASK-231: Form A (Beneficial Owner Declaration) Mapper
 *
 * Maps between ClientComplianceData and FormAData.
 * Used for Swiss banking beneficial owner declarations.
 */

import type { ClientComplianceData, DataSource, TrackedField, BeneficialOwnerData } from '../../../types/clientCompliance';
import { unwrap, createTrackedField, createDataSource } from '../../../types/clientCompliance';
import { transformDate } from '../transformers/dateTransformer';
import { extractCountry } from '../transformers/addressTransformer';
import type { FormAData, MapperOptions } from '../types';
import { joinNames } from './kycMapper';

/**
 * Map ClientComplianceData to FormAData
 *
 * @param data - Shared compliance data
 * @param options - Mapper options
 * @returns Partial Form A data
 */
export function mapToFormA(
  data: ClientComplianceData,
  _options?: MapperOptions
): Partial<FormAData> {
  const personal = data.personal || {};
  const contact = data.contact || {};
  const pep = data.pep || {};

  const firstName = unwrap(personal.firstName) || '';
  const lastName = unwrap(personal.lastName) || '';
  const fullName = joinNames(firstName, lastName);

  const nationalities = unwrap(personal.nationalities) || [];
  const address = unwrap(contact.residentialAddress);
  const domicile = address ? extractCountry(address) : undefined;

  const isPep = unwrap(pep.isPep) || false;
  const pepPosition = unwrap(pep.pepPosition);
  const pepCountry = unwrap(pep.pepCountry);

  // Check if there are third-party beneficial owners
  const hasThirdPartyOwners = data.beneficialOwners && data.beneficialOwners.length > 0;

  // If third-party owners exist, use the first one
  let beneficialOwnerData: FormAData['beneficialOwner'];

  if (hasThirdPartyOwners && data.beneficialOwners[0]) {
    const owner = data.beneficialOwners[0];
    beneficialOwnerData = {
      ownerType: 'third_party',
      name: unwrap(owner.ownerName),
      dateOfBirth: unwrap(owner.ownerDob),
      nationality: unwrap(owner.ownerNationality),
      domicile: unwrap(owner.ownerDomicile),
      percentage: unwrap(owner.ownerPercentage) || 100,
      pep: unwrap(owner.ownerIsPep) || false,
      pepPosition: undefined,
      pepCountry: undefined,
    };
  } else {
    // Self as beneficial owner
    beneficialOwnerData = {
      ownerType: 'self',
      name: fullName || undefined,
      dateOfBirth: unwrap(personal.dateOfBirth),
      nationality: nationalities[0],
      domicile: domicile,
      percentage: 100,
      pep: isPep,
      pepPosition: isPep ? pepPosition : undefined,
      pepCountry: isPep ? pepCountry : undefined,
    };
  }

  return {
    accountHolder: {
      name: fullName || undefined,
      address: address,
    },
    beneficialOwner: beneficialOwnerData,
    declaration: {
      confirmedIdentity: false, // User must confirm
      confirmedBeneficialOwner: false, // User must confirm
      date: undefined,
      signature: undefined,
    },
  };
}

/**
 * Map FormAData back to ClientComplianceData
 *
 * @param formData - Form A form data
 * @param source - Data source for tracking
 * @returns Partial shared compliance data
 */
export function mapFromFormA(
  formData: Partial<FormAData>,
  source: DataSource
): Partial<ClientComplianceData> {
  const accountHolder = formData.accountHolder;
  const beneficialOwner = formData.beneficialOwner;

  const createField = <T>(value: T | undefined): TrackedField<T> | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    return createTrackedField(value as T, source);
  };

  // Parse account holder name
  let firstName: string | undefined;
  let lastName: string | undefined;

  if (accountHolder?.name) {
    const nameParts = accountHolder.name.trim().split(/\s+/);
    firstName = nameParts[0];
    lastName = nameParts.slice(1).join(' ') || undefined;
  }

  const result: Partial<ClientComplianceData> = {
    personal: {
      firstName: createField(firstName),
      lastName: createField(lastName),
    },
    contact: {
      residentialAddress: createField(accountHolder?.address),
    },
  };

  // Handle beneficial owner
  if (beneficialOwner) {
    if (beneficialOwner.ownerType === 'self') {
      // Self as beneficial owner - update personal and PEP data
      if (beneficialOwner.dateOfBirth) {
        result.personal!.dateOfBirth = createField(transformDate(beneficialOwner.dateOfBirth));
      }
      if (beneficialOwner.nationality) {
        result.personal!.nationalities = createField([beneficialOwner.nationality]);
      }

      result.pep = {
        isPep: createField(beneficialOwner.pep),
        pepPosition: createField(beneficialOwner.pepPosition),
        pepCountry: createField(beneficialOwner.pepCountry),
      };
    } else {
      // Third-party beneficial owner
      const ownerData: BeneficialOwnerData = {
        ownerName: createField(beneficialOwner.name),
        ownerDob: createField(beneficialOwner.dateOfBirth ? transformDate(beneficialOwner.dateOfBirth) : undefined),
        ownerNationality: createField(beneficialOwner.nationality),
        ownerDomicile: createField(beneficialOwner.domicile),
        ownerPercentage: createField(beneficialOwner.percentage),
        ownerIsPep: createField(beneficialOwner.pep),
        controlType: createField('direct'),
      };

      result.beneficialOwners = [ownerData];
    }
  }

  return result;
}

/**
 * Create a Form A data source
 */
export function createFormASource(clientId: string, userId?: string): DataSource {
  return createDataSource(
    'manual',
    `forma_${clientId}_${Date.now()}`,
    'Form A',
    1.0,
    undefined,
    userId
  );
}

/**
 * Validate Form A data for submission
 *
 * @param formData - Form A data to validate
 * @returns Validation result with errors if any
 */
export function validateFormA(formData: Partial<FormAData>): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];

  // Account holder validation
  if (!formData.accountHolder?.name) {
    errors.push('Account holder name is required');
  }

  // Beneficial owner validation
  if (!formData.beneficialOwner) {
    errors.push('Beneficial owner information is required');
  } else {
    if (!formData.beneficialOwner.name) {
      errors.push('Beneficial owner name is required');
    }
    if (!formData.beneficialOwner.dateOfBirth) {
      errors.push('Beneficial owner date of birth is required');
    }
    if (!formData.beneficialOwner.nationality) {
      errors.push('Beneficial owner nationality is required');
    }
    if (!formData.beneficialOwner.domicile) {
      errors.push('Beneficial owner domicile is required');
    }
    if (formData.beneficialOwner.percentage === undefined || formData.beneficialOwner.percentage < 0) {
      errors.push('Beneficial owner percentage is required');
    }
  }

  // Declaration validation
  if (!formData.declaration?.confirmedIdentity) {
    errors.push('Identity confirmation is required');
  }
  if (!formData.declaration?.confirmedBeneficialOwner) {
    errors.push('Beneficial owner confirmation is required');
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}
